import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, getPriceTypeLabel, materialStateEnum, priceTypeEnum, stepModeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyToolMutation, useDailyToolMutation } from '../../../../hooks/mutations/useDailyToolMutation';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailyTool } from '../../../../services/apiSLTP/dailyReports/dailyTools';
import { Tool } from '../../../../services/apiSLTP/materials/tools';
import { removeDuplicates } from '../../../../utils/array';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailyToolModalProps extends ModalProps {
    activityId: string;
    canChooseActivity?: boolean;
    refetch: () => void;
    dailyTool: DailyTool | null;
    tool: Tool | null;
    activities: DataItem[];
    site?: { name: string; id: string };
    siteName?: string;
    createTransfer: boolean;
    stepMode?: number;
}

const ManageDailyToolModal: React.FC<ManageDailyToolModalProps> = ({
    open,
    handleClose,
    refetch,
    activityId,
    dailyTool,
    canChooseActivity = false,
    activities,
    tool,
    site,
    siteName,
    createTransfer,

    stepMode,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyToolId, setCurrentDailyToolId] = useState(dailyTool?._id || '');
    const [selectedTool, setSelectedTool] = useState<OptionType | null>(null);
    const [selectedSite, setSelectedSite] = useState<OptionType | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<OptionType | null>(null);
    const [isCreateTransfer, setIsCreateTransfer] = useState(createTransfer);
    const [selectedState, setSelectedState] = useState<OptionType>({
        label: capitalizeFirstLetter(t('present')),
        value: materialStateEnum.PRESENT.toString(),
    });

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];
    const [price, setPrice] = useState(typeof dailyTool?.price === 'number' ? dailyTool?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyTool?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(dailyTool?.priceType))),
                  value: (dailyTool?.priceType).toString(),
              }
            : null
    );

    const { data: dataTools } = useToolsQuery({ queries: { active: true } });
    const { mutate: updateDailyTool } = useDailyToolMutation({ onSuccess: refetch });
    const { mutate: createDailyTool } = useCreateDailyToolMutation({ onSuccess: refetch });

    const postDailyTool = () => {
        if (!selectedTool || !selectedState) return;
        if (canChooseActivity && !selectedActivity) return;
        createDailyTool({
            activityId: activityId || selectedActivity?.value || '',
            toolId: selectedTool?.value,
            state: parseInt(selectedState.value),
            createTransfer: isCreateTransfer,
            isUsed: true,
            quantity: 1,
        });
    };

    const patchDailyTool = () => {
        updateDailyTool({
            id: currentDailyToolId,
            body: {
                toolId: selectedTool?.value,
                state: parseInt(selectedState.value),
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : null,
                priceType: priceType ? parseInt(priceType?.value) : null,
            },
            queries: {
                syncTransfers: true,
            },
        });
    };

    useEffect(() => {
        if (dailyTool) {
            setSelectedTool({ label: dailyTool.toolId?.designation, value: dailyTool.toolId?._id });
            setSelectedState({
                label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyTool.state))),
                value: dailyTool.state.toString(),
            });
        } else if (tool) {
            setSelectedTool({ label: tool?.designation, value: tool?._id });
        }
        if (canChooseActivity && site) {
            setSelectedSite({ label: site?.name, value: site?.id });
        }
    }, []);

    return (
        <ModalBody
            title={t('tool')}
            subtitle={!!currentDailyToolId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyToolId.length ? postDailyTool() : patchDailyTool();
            }}
            disableSubmit={!selectedTool?.value.length || (canChooseActivity && !selectedActivity?.value.length)}
        >
            {canChooseActivity && (
                <>
                    <DropDown
                        selectedItem={selectedSite}
                        items={
                            removeDuplicates(
                                activities.map((activity) => activity.siteId),
                                ['_id']
                            ).map((site) => ({
                                label: site.name,
                                value: site._id,
                            })) || []
                        }
                        handleChange={(event, value) => {
                            setSelectedActivity(null);
                            setSelectedSite(value);
                        }}
                        inputLabel={t('site')}
                        required
                    />
                    {!!selectedSite?.value.length && (
                        <DropDown
                            selectedItem={selectedActivity}
                            items={
                                activities
                                    .filter((activity) => activity.siteId._id === selectedSite.value)
                                    .map((activity) => ({
                                        label: activity.reportId.userId.firstName + ' ' + activity.reportId.userId.lastName,
                                        value: activity._id,
                                    })) || []
                            }
                            handleChange={(event, value) => setSelectedActivity(value)}
                            inputLabel={t('person')}
                            required
                        />
                    )}
                </>
            )}
            <DropDown
                selectedItem={selectedTool}
                items={
                    dataTools?.map((tool) => ({
                        label: tool.designation,
                        value: tool._id,
                        group: tool.toolTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedTool(value)}
                inputLabel={t('tool')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => value ? setSelectedState(value) : null}
                inputLabel={t('state')}
                required
            />
            {stepMode !== stepModeEnum.VALIDATE && (
                <ToggleLine
                    checked={isCreateTransfer}
                    onChange={(checked) => setIsCreateTransfer(checked)}
                    title={capitalizeFirstLetter(t('createTransfer'))}
                    disabled={parseInt(selectedState.value) === materialStateEnum.PRESENT}
                />
            )}
            {stepMode === stepModeEnum.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                        placeholder={`${t('default')} - ${dailyTool?.toolId.price ?? dailyTool?.toolId.toolTypeId.price}`}
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                        placeholder={`${t('default')} - ${capitalizeFirstLetter(t(getPriceTypeLabel(dailyTool?.toolId.priceType ?? dailyTool?.toolId.toolTypeId.priceType)))}`}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyToolModal;
